/**
 * Documents Service
 * API service for Document Management system with search, filtering, upload, and access control
 */

// Document interfaces
export interface DocumentCategory {
  id: number;
  name: string;
  code: string;
  category_type: string;
  description: string;
  requires_approval: boolean;
  retention_period_years: number;
  priority_level: string;
  default_access_level: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  document_types_count: number;
}

export interface DocumentType {
  id: number;
  name: string;
  code: string;
  description: string;
  category: number;
  category_name: string;
  category_code: string;
  supports_versioning: boolean;
  requires_technical_review: boolean;
  requires_safety_review: boolean;
  allowed_file_extensions: string[];
  max_file_size_mb: number;
  template_file?: string;
  naming_convention: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  documents_count: number;
}

export interface Document {
  id: number;
  title: string;
  document_number: string;
  version: string;
  status: 'draft' | 'under_review' | 'approved' | 'published' | 'archived' | 'obsolete';
  access_level: 'public' | 'restricted' | 'confidential' | 'classified';
  document_type: number;
  document_type_name: string;
  document_type_code: string;
  category_name: string;
  description: string;
  keywords: string;
  file: string;
  file_size_bytes: number;
  file_hash: string;
  
  // Associations
  asset_owner?: number;
  asset_owner_name?: string;
  site?: number;
  site_name?: string;
  site_code?: string;
  site_unit?: number;
  site_unit_name?: string;
  site_subsystem?: number;
  subsystem_name?: string;
  equipment: number[];
  equipment_list: EquipmentReference[];
  
  // Dates
  created_date: string;
  published_date?: string;
  effective_date?: string;
  expiry_date?: string;
  next_review_date?: string;
  
  // Users
  created_by: number;
  created_by_name: string;
  approved_by?: number;
  approved_by_name?: string;
  approved_date?: string;
  
  // Document relationships
  supersedes?: number;
  supersedes_document?: DocumentReference;
  superseded_by_document?: DocumentReference;
  related_documents: number[];
  related_documents_list: DocumentReference[];
  
  // Computed properties
  association_level: string;
  association_display: string;
  file_extension: string;
  file_size_display: string;
  is_expired: boolean;
  needs_review: boolean;
  
  updated_at: string;
  is_active: boolean;
}

export interface EquipmentReference {
  id: number;
  kks_code: string;
  name: string;
  equipment_type: string;
}

export interface DocumentReference {
  id: number;
  document_number: string;
  title: string;
  version: string;
  status: string;
}

export interface DocumentVersion {
  id: number;
  document: number;
  document_number: string;
  document_title: string;
  version_number: string;
  created_date: string;
  created_by: number;
  created_by_name: string;
  change_summary: string;
  change_reason: string;
  file_snapshot: string;
  file_size_bytes: number;
  file_size_display: string;
  file_hash: string;
  status_at_creation: string;
  reviewed_by?: number;
  reviewed_by_name?: string;
  reviewed_date?: string;
  review_comments: string;
  approved_by?: number;
  approved_by_name?: string;
  approved_date?: string;
  approval_comments: string;
  is_current: boolean;
  is_published: boolean;
}

export interface DocumentAccess {
  id: number;
  document: number;
  document_number: string;
  document_title: string;
  user?: number;
  user_name?: string;
  role: string;
  access_type: 'view' | 'download' | 'edit' | 'approve' | 'admin';
  access_subject: string;
  valid_from?: string;
  valid_until?: string;
  download_limit?: number;
  ip_restrictions: string[];
  granted_by: number;
  granted_by_name: string;
  granted_date: string;
  revoked_date?: string;
  revoked_by?: number;
  revoked_by_name?: string;
  download_count: number;
  last_accessed?: string;
  is_active: boolean;
  is_valid: boolean;
}

export interface DocumentSearchParams {
  search?: string;
  document_type?: number;
  category?: number;
  status?: string;
  access_level?: string;
  site?: number;
  equipment?: number;
  created_by?: number;
  date_from?: string;
  date_to?: string;
  expired?: boolean;
  needs_review?: boolean;
  file_extension?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface DocumentStatistics {
  total_documents: number;
  by_status: Record<string, number>;
  by_category: Record<string, number>;
  by_access_level: Record<string, number>;
  expired_count: number;
  needs_review_count: number;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

class DocumentsService {
  private baseURL = '/api/v1';

  private async getAuthHeaders(): Promise<Record<string, string>> {
    // Get CSRF token
    const csrfResponse = await fetch(`${this.baseURL}/users/csrf/`);
    const csrfData = await csrfResponse.json();
    
    return {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfData.csrfToken,
    };
  }

  private async apiCall<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      credentials: 'include',
      headers: {
        ...headers,
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`API call failed: ${response.status} ${response.statusText} - ${errorData}`);
    }

    return response.json();
  }

  private async uploadCall<T>(endpoint: string, formData: FormData): Promise<T> {
    // Get CSRF token for file uploads
    const csrfResponse = await fetch(`${this.baseURL}/users/csrf/`);
    const csrfData = await csrfResponse.json();
    
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-CSRFToken': csrfData.csrfToken,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorData}`);
    }

    return response.json();
  }

  // Document Categories
  async getDocumentCategories(): Promise<DocumentCategory[]> {
    return this.apiCall<DocumentCategory[]>('/sites/document-categories/');
  }

  async getDocumentCategory(id: number): Promise<DocumentCategory> {
    return this.apiCall<DocumentCategory>(`/sites/document-categories/${id}/`);
  }

  async getCategoryDocumentTypes(categoryId: number): Promise<DocumentType[]> {
    return this.apiCall<DocumentType[]>(`/sites/document-categories/${categoryId}/document_types/`);
  }

  // Document Types
  async getDocumentTypes(): Promise<DocumentType[]> {
    return this.apiCall<DocumentType[]>('/sites/document-types/');
  }

  async getDocumentType(id: number): Promise<DocumentType> {
    return this.apiCall<DocumentType>(`/sites/document-types/${id}/`);
  }

  async getTypeDocuments(typeId: number): Promise<Document[]> {
    return this.apiCall<Document[]>(`/sites/document-types/${typeId}/documents/`);
  }

  // Documents
  async getDocuments(params?: DocumentSearchParams): Promise<PaginatedResponse<Document>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      for (const key in params) {
        if (params.hasOwnProperty(key)) {
          const value = params[key as keyof DocumentSearchParams];
          if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, value.toString());
          }
        }
      }
    }
    
    const endpoint = `/sites/documents/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.apiCall<PaginatedResponse<Document>>(endpoint);
  }

  async getDocument(id: number): Promise<Document> {
    return this.apiCall<Document>(`/sites/documents/${id}/`);
  }

  async searchDocuments(params: DocumentSearchParams): Promise<PaginatedResponse<Document>> {
    const queryParams = new URLSearchParams();
    
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        const value = params[key as keyof DocumentSearchParams];
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      }
    }
    
    return this.apiCall<PaginatedResponse<Document>>(`/sites/documents/search/?${queryParams.toString()}`);
  }

  async createDocument(documentData: FormData): Promise<Document> {
    return this.uploadCall<Document>('/sites/documents/', documentData);
  }

  async updateDocument(id: number, documentData: FormData): Promise<Document> {
    // Get CSRF token for file uploads
    const csrfResponse = await fetch(`${this.baseURL}/users/csrf/`);
    const csrfData = await csrfResponse.json();
    
    const response = await fetch(`${this.baseURL}/sites/documents/${id}/`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'X-CSRFToken': csrfData.csrfToken,
      },
      body: documentData,
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Update failed: ${response.status} ${response.statusText} - ${errorData}`);
    }

    return response.json();
  }

  async deleteDocument(id: number): Promise<void> {
    await this.apiCall(`/sites/documents/${id}/`, { method: 'DELETE' });
  }

  async downloadDocument(id: number): Promise<Blob> {
    // Get CSRF token
    const csrfResponse = await fetch(`${this.baseURL}/users/csrf/`);
    const csrfData = await csrfResponse.json();
    
    const response = await fetch(`${this.baseURL}/sites/documents/${id}/download/`, {
      credentials: 'include',
      headers: {
        'X-CSRFToken': csrfData.csrfToken,
      },
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }

    return response.blob();
  }

  async approveDocument(id: number): Promise<Document> {
    return this.apiCall<Document>(`/sites/documents/${id}/approve/`, { method: 'POST' });
  }

  async getDocumentVersions(id: number): Promise<DocumentVersion[]> {
    return this.apiCall<DocumentVersion[]>(`/sites/documents/${id}/versions/`);
  }

  async createDocumentVersion(id: number, versionData: FormData): Promise<DocumentVersion> {
    return this.uploadCall<DocumentVersion>(`/sites/documents/${id}/create_version/`, versionData);
  }

  async getDocumentAuditLog(id: number): Promise<any[]> {
    return this.apiCall<any[]>(`/sites/documents/${id}/audit_log/`);
  }

  async getDocumentStatistics(): Promise<DocumentStatistics> {
    return this.apiCall<DocumentStatistics>('/sites/documents/statistics/');
  }

  // Equipment-specific documents
  async getEquipmentDocuments(equipmentId: number): Promise<Document[]> {
    const response = await this.apiCall<PaginatedResponse<Document>>(`/sites/documents/?equipment=${equipmentId}`);
    // Handle both paginated and direct array responses
    if (response && typeof response === 'object' && 'results' in response) {
      return response.results || [];
    }
    // If it's already an array, return it
    return Array.isArray(response) ? response : [];
  }

  // Site-specific documents
  async getSiteDocuments(siteId: number): Promise<Document[]> {
    const response = await this.apiCall<PaginatedResponse<Document>>(`/sites/documents/?site=${siteId}`);
    // Handle both paginated and direct array responses
    if (response && typeof response === 'object' && 'results' in response) {
      return response.results || [];
    }
    // If it's already an array, return it
    return Array.isArray(response) ? response : [];
  }

  // Document Access Management
  async getDocumentAccess(documentId: number): Promise<DocumentAccess[]> {
    return this.apiCall<DocumentAccess[]>(`/sites/document-access/?document=${documentId}`);
  }

  async grantDocumentAccess(accessData: any): Promise<DocumentAccess> {
    return this.apiCall<DocumentAccess>('/sites/document-access/', {
      method: 'POST',
      body: JSON.stringify(accessData),
    });
  }

  async revokeDocumentAccess(accessId: number): Promise<DocumentAccess> {
    return this.apiCall<DocumentAccess>(`/sites/document-access/${accessId}/revoke/`, {
      method: 'POST',
    });
  }

  // Helper methods
  getFileIconClass(fileExtension: string): string {
    const iconMap: Record<string, string> = {
      pdf: 'fas fa-file-pdf text-red-500',
      doc: 'fas fa-file-word text-blue-500',
      docx: 'fas fa-file-word text-blue-500',
      xls: 'fas fa-file-excel text-green-500',
      xlsx: 'fas fa-file-excel text-green-500',
      ppt: 'fas fa-file-powerpoint text-orange-500',
      pptx: 'fas fa-file-powerpoint text-orange-500',
      dwg: 'fas fa-file-image text-purple-500',
      dxf: 'fas fa-file-image text-purple-500',
    };
    
    return iconMap[fileExtension.toLowerCase()] || 'fas fa-file text-gray-500';
  }

  getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      draft: 'gray',
      under_review: 'orange',
      approved: 'green',
      published: 'blue',
      archived: 'purple',
      obsolete: 'red',
    };
    
    return colorMap[status] || 'gray';
  }

  getAccessLevelColor(accessLevel: string): string {
    const colorMap: Record<string, string> = {
      public: 'green',
      restricted: 'orange',
      confidential: 'red',
      classified: 'purple',
    };
    
    return colorMap[accessLevel] || 'gray';
  }
}

export const documentsService = new DocumentsService(); 